import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import TemplateEditor from '../components/forms/TemplateEditor';
import { canManageEmailTemplate } from '../utils/emailAccess';
import { useAuth } from '../components/auth/AuthContext';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { logUserAction } from '../utils/logger';
import { API_URL } from '../utils/apiConfig';
import { EMAIL_TEMPLATE_VARIABLES } from '../utils/emailTemplateVariables';
import { formatDistanceToNow } from 'date-fns';

const EmailTemplatesPage = () => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testTemplate, setTestTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [creatorFilter, setCreatorFilter] = useState('all'); // 'all' or 'mine'
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' or 'archived'
  const [sortField, setSortField] = useState(null); // 'name', 'category', 'createdBy', 'createdAt'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    const unsub = onSnapshot(
      query(
        collection(db, collectionPath('emailTemplates')),
        orderBy('createdAt', 'desc')
      ),
      (snap) => {
        const loadedTemplates = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Log templates to help debug duplicates
        console.log('Loaded templates:', loadedTemplates.length, 'total');
        console.log('My templates:', loadedTemplates.filter(t => t.createdBy === currentUser?.uid).map(t => ({ 
          id: t.id, 
          name: t.name, 
          createdBy: t.createdBy,
          createdByName: t.createdByName 
        })));
        setTemplates(loadedTemplates);
        setLoading(false);
      },
      (error) => {
        console.error('Failed to load templates:', error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [currentUser]);

  const handleArchive = async (template) => {
    try {
      await updateDoc(doc(db, collectionPath('emailTemplates'), template.id), {
        isArchived: !template.isArchived
      });
      const archiveAction = template.isArchived ? 'Email Template Restored' : 'Email Template Archived';
      await logUserAction({ action: archiveAction, details: `Template ${template.isArchived ? 'restored' : 'archived'}: "${template.name}"`, entityType: 'EmailTemplate', entityId: template.id });
      setToast({ 
        message: template.isArchived ? 'Template restored' : 'Template archived', 
        type: 'success' 
      });
    } catch (error) {
      setToast({ message: 'Failed to update template', type: 'error' });
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Permanently delete template "${template.name}"?`)) return;

    try {
      await deleteDoc(doc(db, collectionPath('emailTemplates'), template.id));
      await logUserAction({ action: 'Email Template Deleted', details: `Template deleted: "${template.name}"`, entityType: 'EmailTemplate', entityId: template.id });
      setToast({ message: 'Template deleted', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to delete template', type: 'error' });
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      setToast({ message: 'Please enter a recipient email', type: 'error' });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail.trim())) {
      setToast({ message: 'Please enter a valid email address', type: 'error' });
      return;
    }

    setSendingTest(true);
    try {
      const response = await fetch(`${API_URL}/api/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: testEmail.trim(),
          subject: testTemplate.subject || '[TEST] Email Template',
          htmlContent: testTemplate.htmlContent || '',
          textContent: testTemplate.textContent || '',
          variables: EMAIL_TEMPLATE_VARIABLES.reduce((acc, v) => {
            acc[v.name] = v.example;
            return acc;
          }, {})
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setToast({ message: `Test email sent to ${testEmail}`, type: 'success' });
        setShowTestModal(false);
        setTestTemplate(null);
        setTestEmail('');
      } else {
        throw new Error(result.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test email error:', error);
      setToast({ message: `Failed to send test: ${error.message}`, type: 'error' });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading templates...</div>
        </div>
      </main>
    );
  }

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter templates based on search term, creator filter, and status
  let filteredTemplates = templates.filter(template => {
    // Apply status filter (active/archived)
    if (statusFilter === 'active' && template.isArchived) {
      return false;
    }
    if (statusFilter === 'archived' && !template.isArchived) {
      return false;
    }
    
    // Apply creator filter (all/mine)
    if (creatorFilter === 'mine') {
      // "My Templates" shows only templates created by current user
      // Legacy templates without createdBy are excluded from "My Templates"
      if (template.createdBy !== currentUser?.uid) {
        return false;
      }
    } else {
      // "All Templates" shows public templates + user's private templates
      // Hide private templates that belong to other users
      // Legacy templates (no createdBy) are treated as public
      if (template.isPrivate && template.createdBy && template.createdBy !== currentUser?.uid) {
        return false;
      }
    }
    
    // Apply search filter
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      template.name?.toLowerCase().includes(searchLower) ||
      template.subject?.toLowerCase().includes(searchLower) ||
      template.category?.toLowerCase().includes(searchLower)
    );
  });

  // Apply sorting
  if (sortField) {
    filteredTemplates = [...filteredTemplates].sort((a, b) => {
      let aValue, bValue;
      
      if (sortField === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (sortField === 'category') {
        aValue = (a.category || '').toLowerCase();
        bValue = (b.category || '').toLowerCase();
      } else if (sortField === 'createdBy') {
        aValue = (a.createdByName || a.createdBy || 'zzz').toLowerCase();
        bValue = (b.createdByName || b.createdBy || 'zzz').toLowerCase();
      } else if (sortField === 'createdAt') {
        aValue = a.createdAt?.toDate?.() || new Date(0);
        bValue = b.createdAt?.toDate?.() || new Date(0);
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Count templates by status and creator (context-aware based on current statusFilter)
  // For "My Templates" - only count templates created by current user in the current status
  const myTemplatesCount = templates.filter(t => {
    const isMyTemplate = t.createdBy === currentUser?.uid;
    const matchesStatus = statusFilter === 'active' ? !t.isArchived : t.isArchived;
    return isMyTemplate && matchesStatus;
  }).length;
  
  // For "All Templates" - count public + user's private + legacy in current status
  const allTemplatesCount = templates.filter(t => {
    const isVisible = !t.isPrivate || !t.createdBy || t.createdBy === currentUser?.uid;
    const matchesStatus = statusFilter === 'active' ? !t.isArchived : t.isArchived;
    return isVisible && matchesStatus;
  }).length;
  
  // Active tab count - based on current creator filter
  const activeCount = templates.filter(t => {
    if (!t.isArchived) {
      if (creatorFilter === 'mine') {
        return t.createdBy === currentUser?.uid;
      } else {
        return !t.isPrivate || !t.createdBy || t.createdBy === currentUser?.uid;
      }
    }
    return false;
  }).length;
  
  // Archived tab count - based on current creator filter
  const archivedCount = templates.filter(t => {
    if (t.isArchived) {
      if (creatorFilter === 'mine') {
        return t.createdBy === currentUser?.uid;
      } else {
        return !t.isPrivate || !t.createdBy || t.createdBy === currentUser?.uid;
      }
    }
    return false;
  }).length;

  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Email Templates</h2>
        {can(FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE) && (
        <button
          onClick={() => {
            setEditTemplate(null);
            setShowEditor(true);
          }}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Create Template
        </button>
        )}
      </div>

      {/* Status Tabs (Active/Archived) */}
      <div className="flex space-x-4 mb-4 border-b">
        {['active', 'archived'].map(status => {
          const count = status === 'active' ? activeCount : archivedCount;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 ${
                statusFilter === status
                  ? 'border-b-2 border-indigo-600 text-indigo-600 font-semibold'
                  : 'text-gray-500'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {count > 0 && (
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                  statusFilter === status ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-4 flex gap-4 items-center">
        <select
          value={creatorFilter}
          onChange={(e) => setCreatorFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Templates ({allTemplatesCount})</option>
          <option value="mine">My Templates ({myTemplatesCount})</option>
        </select>
        
        <input
          type="text"
          placeholder="Search templates by name, subject, or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`flex-1 px-4 py-2 border rounded-lg transition-colors duration-150 ${
            search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
          }`}
        />
        
        {search && (
          <div className="text-sm text-gray-600 whitespace-nowrap">
            {filteredTemplates.length} of {statusFilter === 'active' ? activeCount : archivedCount}
          </div>
        )}
      </div>

      {/* Templates List */}
      <div>
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">
              {statusFilter === 'active' && templates.filter(t => !t.isArchived).length === 0
                ? 'No templates yet. Create your first template to get started.'
                : 'No templates match your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Template Name
                      {sortField === 'name' && (
                        <span className="text-indigo-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center gap-1">
                      Category
                      {sortField === 'category' && (
                        <span className="text-indigo-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center gap-1">
                      Created Date
                      {sortField === 'createdAt' && (
                        <span className="text-indigo-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('createdBy')}
                  >
                    <div className="flex items-center gap-1">
                      Created By
                      {sortField === 'createdBy' && (
                        <span className="text-indigo-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTemplates.map(template => (
                  <tr 
                    key={template.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {template.thumbnailUrl && (
                          <img
                            src={template.thumbnailUrl}
                            alt={template.name}
                            className="w-16 h-12 object-cover rounded mr-3"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            <Tooltip text={
                              template.description ? (
                                <div className="text-left">
                                  <div className="font-semibold mb-1">{template.name}</div>
                                  <div className="text-sm text-gray-200">{template.description}</div>
                                </div>
                              ) : template.name
                            } maxWidth={400}>
                              <span>{template.name}</span>
                            </Tooltip>
                            {template.isPrivate && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                                🔒 Private
                              </span>
                            )}
                          </div>
                          {template.variables && template.variables.length > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              Variables: {template.variables.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                        {template.category ? template.category.charAt(0).toUpperCase() + template.category.slice(1).toLowerCase() : 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {template.subject || 'No subject'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {template.createdAt ? new Date(template.createdAt.toDate()).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        }) : 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {template.createdByName || (template.createdBy ? 'Unknown User' : 'Legacy Template')}
                      </div>
                      {template.lastEditedBy && (
                        <div className="text-xs text-gray-500 mt-1">
                          Last edited by: {template.lastEditedByName || 'Unknown'}
                          {template.updatedAt && (
                            <span className="ml-1">
                              ({formatDistanceToNow(template.updatedAt.toDate(), { addSuffix: true })})
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setPreviewTemplate(template)}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                          disabled={!template.htmlContent}
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => {
                            if (canManageEmailTemplate(currentUser, can, template, FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE)) {
                              setEditTemplate(template);
                              setShowEditor(true);
                            } else {
                              setPreviewTemplate(template);
                            }
                          }}
                          className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                        >
                          {canManageEmailTemplate(currentUser, can, template, FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE) ? 'Edit' : 'View'}
                        </button>
                        {canManageEmailTemplate(currentUser, can, template, FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE) && (
                        <>
                        <button
                          onClick={() => handleArchive(template)}
                          className={`px-3 py-1 border rounded transition-colors ${
                            template.isArchived 
                              ? 'border-green-300 text-green-600 hover:bg-green-50'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {template.isArchived ? 'Restore' : 'Archive'}
                        </button>
                        {canManageEmailTemplate(currentUser, can, template, FEATURE_PERMISSIONS.EMAIL_DELETE_TEMPLATE) && (
                        <button
                          onClick={() => handleDelete(template)}
                          className="px-3 py-1 border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                        )}
                        </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditor && (
        <TemplateEditor
          template={editTemplate}
          onClose={() => {
            setShowEditor(false);
            setEditTemplate(null);
          }}
          onSaved={(message) => {
            setShowEditor(false);
            setEditTemplate(null);
            setToast({ message, type: 'success' });
          }}
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewTemplate(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {previewTemplate.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {previewTemplate.subject}
                </p>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Preview */}
            <div className="flex-1 overflow-auto p-4">
              {previewTemplate.htmlContent ? (
                <iframe
                  srcDoc={previewTemplate.htmlContent}
                  className="w-full h-full min-h-[600px] border rounded"
                  title="Template Preview"
                  sandbox="allow-same-origin allow-scripts"
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-400">
                  No preview available
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setTestTemplate(previewTemplate);
                  setTestEmail(currentUser?.email || '');
                  setShowTestModal(true);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Test
              </button>
              <button
                onClick={() => {
                  setEditTemplate(previewTemplate);
                  setShowEditor(true);
                  setPreviewTemplate(null);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                Edit Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Modal */}
      {showTestModal && testTemplate && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!sendingTest) {
              setShowTestModal(false);
              setTestTemplate(null);
              setTestEmail('');
            }
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Send Test Email
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {testTemplate.name}
                </p>
              </div>
              <button
                onClick={() => {
                  if (!sendingTest) {
                    setShowTestModal(false);
                    setTestTemplate(null);
                    setTestEmail('');
                  }
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={sendingTest}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Subject: <span className="font-medium">{testTemplate.subject}</span>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  This will send a test email with sample variable values.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={sendingTest}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !sendingTest) {
                      handleSendTest();
                    }
                  }}
                />
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-1">Test Variables:</p>
                <ul className="text-xs text-gray-600 space-y-0.5">
                  {EMAIL_TEMPLATE_VARIABLES.map(v => (
                    <li key={v.name}>• {v.label}: {v.example}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestTemplate(null);
                  setTestEmail('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors"
                disabled={sendingTest}
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                disabled={sendingTest || !testEmail.trim()}
              >
                {sendingTest ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </main>
  );
};

export default EmailTemplatesPage;
